'use client';
import { useState, useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';




import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useCounterAnimation } from '@/hooks/useCounterAnimation';

const faqData = [
  {
    id: 'faq-1',
    question: 'Do I work directly with Daniel?',
    answer: 'Yes. Daniel leads strategy, planning, and client communication on all engagements. Depending on your capacity bucket, you may also work with specialized collaborators for production or technical execution — but Daniel remains your primary point of contact and oversees all work.',
  },
  {
    id: 'faq-2',
    question: 'What size businesses are a fit?',
    answer: 'Best Odds Corp works best with established businesses doing $500k–$10M+ in revenue who have momentum but are hitting growth bottlenecks — revenue leaks, weak positioning, poor follow-up, or execution gaps. Ideal for founders, CEOs, and operators who want a focused, accountable partner — not a broad agency with a menu of disconnected services.',
  },
  {
    id: 'faq-3',
    question: 'Is this separate from normal Best Odds services?',
    answer: 'Yes. Private consulting with Daniel is a distinct, founder-led advisory offer. It is not a productized service from the catalog. It is private, limited, and designed for businesses that want high-level strategic access rather than execution-only deliverables.',
  },
  {
    id: 'faq-4',
    question: 'Can this lead into implementation?',
    answer: 'Absolutely. Some clients use advisory sessions to shape strategy, then engage Best Odds Corp for execution across marketing, creative, websites, funnels, content, or operations. Strategy and execution can work together.',
  },
  {
    id: 'faq-5',
    question: 'What\'s the first step?',
    answer: 'Start with a Sprint. It\'s a paid diagnostic engagement that audits your business, identifies the highest-value bottlenecks, and delivers a prioritized roadmap — before you commit to anything else. After the Sprint, you choose the path that fits: take the roadmap and execute internally, engage us for a fixed-scope buildout, or move into ongoing execution. Book a call and we\'ll walk through whether a Sprint is the right fit for your situation.',
  },
  {
    id: 'faq-6',
    question: 'Can you work with my in-house team?',
    answer: 'Absolutely. Many clients use Best Odds Corp to extend their internal team\'s capacity. We integrate with your existing tools (Slack, Asana, Google Drive, etc.) and can collaborate with your designers, developers, or other agencies. We\'re built to be a seamless extension of your marketing function.',
  },
  {
    id: 'faq-7',
    question: 'Do you guarantee results?',
    answer: 'No. We don\'t make guarantees because marketing outcomes depend on many variables outside our control (product-market fit, pricing, offer quality, market conditions). What we do guarantee: consistent execution, weekly shipping, transparent reporting, and accountability. We focus on doing the work right and iterating based on data.',
  },
];

export default function WorkWithDanielPage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const toggleFaq = (id: string) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  const capabilities = [
    {
      id: 'cap_1',
      icon: 'LightBulbIcon',
      title: 'Bottleneck Diagnosis',
      description: 'Identifies the real constraint — not the symptom. Audits operations, marketing, and offer clarity to find where revenue is leaking.'
    },
    {
      id: 'cap_2',
      icon: 'CpuChipIcon',
      title: 'AI Systems & Automation',
      description: 'Builds workflows, content engines, and automation using AI — reducing manual work without losing quality or brand voice.'
    },
    {
      id: 'cap_3',
      icon: 'MegaphoneIcon',
      title: 'Growth Marketing Execution',
      description: 'Paid search, paid social, SEO, and content — executed in weekly sprints with transparent reporting and real optimization.'
    },
    {
      id: 'cap_4',
      icon: 'GlobeAltIcon',
      title: 'Web, Funnel & Tool Builds',
      description: 'Websites, landing pages, Chrome extensions, and internal tools — built fast using AI leverage and operator judgment.'
    },
    {
      id: 'cap_5',
      icon: 'ChartBarIcon',
      title: 'Offer & Positioning Clarity',
      description: 'Tightens messaging, sharpens the offer, and aligns positioning so the right buyers convert without confusion.'
    },
    {
      id: 'cap_6',
      icon: 'DocumentTextIcon',
      title: 'Content & Distribution Systems',
      description: 'Builds repeatable content systems across platforms — including X, Afterhour, and owned channels — that compound over time.'
    }
  ];

  const credentials = [
    {
      id: 'cred_1',
      icon: 'AcademicCapIcon',
      label: 'Education',
      value: 'B.S. Marketing & Business Communication',
      sub: 'The College of St. Scholastica'
    },
    {
      id: 'cred_2',
      icon: 'BuildingOfficeIcon',
      label: 'Founded',
      value: 'Best Odds Corp — Est. 2000+',
      sub: '20+ years of business evolution'
    },
    {
      id: 'cred_3',
      icon: 'UsersIcon',
      label: 'Afterhour Following',
      value: '135,658+',
      sub: 'Active finance & trading audience'
    },
    {
      id: 'cred_4',
      icon: 'ChatBubbleLeftRightIcon',
      label: 'X (Twitter) Following',
      value: '17,186+',
      sub: 'Content creator & operator presence'
    }
  ];

  const operatorTraits = [
    {
      id: 'trait_1',
      icon: 'BoltIcon',
      title: 'Execution Speed',
      description: 'Ships weekly. Moves fast without sacrificing quality. Prefers direct action over prolonged planning cycles.'
    },
    {
      id: 'trait_2',
      icon: 'MagnifyingGlassIcon',
      title: 'Pattern Recognition',
      description: 'Spots bottlenecks, revenue leaks, and positioning gaps quickly — built from 20+ years of building and testing across industries.'
    },
    {
      id: 'trait_3',
      icon: 'CpuChipIcon',
      title: 'AI Tool Fluency',
      description: 'Uses AI across every workflow — content, code, research, automation. Built the current Best Odds website and tools using AI as leverage.'
    },
    {
      id: 'trait_4',
      icon: 'ChartBarIcon',
      title: 'Decision-Making Under Uncertainty',
      description: 'Active trader and operator. Comfortable making high-judgment calls with incomplete information — a skill that transfers directly to business execution.'
    },
    {
      id: 'trait_5',
      icon: 'LightBulbIcon',
      title: 'Marketing Judgment',
      description: 'Formally trained in marketing and business communication. Combines academic foundation with 20+ years of real-world testing and iteration.'
    },
    {
      id: 'trait_6',
      icon: 'WrenchScrewdriverIcon',
      title: 'Systems & Product Thinking',
      description: 'Thinks in systems, not tasks. Builds repeatable processes, automations, and tools that reduce dependency on manual effort.'
    }
  ];

  const workingPrinciples = [
    {
      id: 'principle_1',
      title: 'Direct, fast communication',
      description: 'No account manager layers. You work directly with the operator making decisions.'
    },
    {
      id: 'principle_2',
      title: 'Weekly planning + shipping cadence',
      description: 'Sprints are planned Monday, executed through the week, reviewed Friday. Consistent rhythm.'
    },
    {
      id: 'principle_3',
      title: 'Clear approvals and scope control',
      description: 'You approve before we build. No surprise invoices, no scope creep without discussion.'
    },
    {
      id: 'principle_4',
      title: 'No fluff, results-first',
      description: 'Focus on what moves the needle. Skip vanity metrics and theater work.'
    }
  ];

  const engagementOptions = [
    {
      id: 'eng-1',
      number: '01',
      title: 'Private Strategy Session',
      description: 'A focused session to identify bottlenecks, opportunities, and the highest-leverage next moves.',
    },
    {
      id: 'eng-2',
      number: '02',
      title: 'Founder-Led Advisory',
      description: 'Ongoing direct access for businesses that want continued guidance on AI, systems, marketing, and execution.',
    },
    {
      id: 'eng-3',
      number: '03',
      title: 'Project-Guided Implementation',
      description: 'Advisory combined with a Best Odds service engagement when strategy needs to turn into shipped work.',
    },
  ];

  const pricingBlocks = [
    {
      id: 'price-1',
      number: '01',
      title: 'Private Strategy Session',
      description: 'A paid, high-value working session designed to identify bottlenecks, leverage points, AI opportunities, and the highest-value next moves.',
      pricing: 'Pricing shared after fit review.',
    },
    {
      id: 'price-2',
      number: '02',
      title: 'Founder-Led Advisory',
      description: 'Ongoing direct access for businesses that want continued guidance on AI, systems, marketing, workflow design, and strategic execution.',
      pricing: 'Custom scope based on business needs.',
    },
    {
      id: 'price-3',
      number: '03',
      title: 'Project-Guided Implementation',
      description: 'For companies that want strategy translated into execution through a broader Best Odds Corp engagement.',
      pricing: 'Quoted separately based on project scope.',
    },
  ];

  const whoItsFor = [
    'Founders who want direct strategic help',
    'Operators trying to use AI more effectively',
    'Teams with messy workflows or weak handoffs',
    'Businesses that need leverage, clarity, and speed',
    'Companies that want a real builder, not surface-level AI chatter',
    'Brands that value strategy, execution, and taste',
  ];

  function AnimatedCredStat({ value, suffix, label }: { value: number; suffix?: string; label: string }) {
    const { displayValue, elementRef } = useCounterAnimation({
      end: value,
      duration: 2000,
      suffix: suffix || '',
    });
    return (
      <div ref={elementRef} className="reveal text-center p-8 border border-white/10">
        <div className="text-5xl font-bold text-[#DC2626] mb-2">{displayValue}</div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">{label}</p>
      </div>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#0A0A0A] text-white pt-24">

        {/* ── 1. HERO ─────────────────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left: Content */}
              <div className="reveal">
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                  Meet the Founder
                </span>
                <h1 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                  Daniel Moravec
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 mb-8 leading-relaxed font-light">
                  Founder. Operator. AI-Powered Execution Partner.
                </p>
                <p className="text-body text-gray-400 mb-8 leading-relaxed">
                  Daniel founded Best Odds Corp over 20 years ago and has spent the time since building, selling, testing, automating, and repositioning businesses across multiple industries. Formally trained in Marketing and Business Communication, he now combines that foundation with deep AI fluency, active trading discipline, and a sprint-based execution model built for founders who need results — not more strategy decks.
                </p>

                {/* Proof Chips */}
                <div className="flex flex-wrap gap-3 mb-10">
                  <span className="text-[10px] uppercase tracking-widest border border-white/20 px-3 py-1.5 text-gray-300">Founded 2000+</span>
                  <span className="text-[10px] uppercase tracking-widest border border-white/20 px-3 py-1.5 text-gray-300">Las Vegas, NV</span>
                  <span className="text-[10px] uppercase tracking-widest border border-white/20 px-3 py-1.5 text-gray-300">B.S. Marketing & Business Communication</span>
                  <span className="text-[10px] uppercase tracking-widest border border-white/20 px-3 py-1.5 text-gray-300">AI-Fluent Operator</span>
                </div>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link href="/book">
                    <button className="px-8 py-4 bg-[#DC2626] text-white text-[10px] uppercase tracking-widest font-semibold hover:bg-[#b91c1c] transition-colors">
                      Book a Sprint
                    </button>
                  </Link>
                  <Link href="/services">
                    <button className="px-8 py-4 border border-white/20 text-white text-[10px] uppercase tracking-widest font-semibold hover:border-white/50 transition-colors">
                      View Services
                    </button>
                  </Link>
                </div>

                {/* Social Trust Signals */}
                <div className="flex flex-wrap items-center gap-4 pt-8 border-t border-white/10">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-gray-500">Find Daniel on</span>
                  <a
                    href="https://afterhour.com/adioking"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-widest text-white border border-white/20 px-4 py-2 hover:border-[#DC2626] hover:text-[#DC2626] transition-colors font-medium"
                  >
                    Afterhour — @adioking
                  </a>
                  <a
                    href="https://x.com/adioking"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] uppercase tracking-widest text-white border border-white/20 px-4 py-2 hover:border-[#DC2626] hover:text-[#DC2626] transition-colors font-medium"
                  >
                    X — @adioking
                  </a>
                </div>
              </div>

              {/* Right: Headshot */}
              <div className="reveal">
                <div className="aspect-square bg-[#111] border border-white/10 overflow-hidden">
                  <AppImage
                    src="/assets/images/Untitled-1771493284658.jpeg"
                    alt="Daniel Moravec - Founder of Best Odds Corp"
                    width={800}
                    height={800}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 2. CREDENTIALS STRIP ────────────────────────────────────────── */}
        <section className="py-16 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {credentials.map((cred) => (
                <div key={cred.id} className="reveal flex items-start gap-4 p-6 border border-white/10 bg-[#0A0A0A]">
                  <div className="w-10 h-10 border border-[#DC2626]/40 flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name={cred.icon as any} size={20} className="text-[#DC2626]" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-1">{cred.label}</span>
                    <p className="text-sm font-semibold text-white mb-1">{cred.value}</p>
                    <p className="text-xs text-gray-500">{cred.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 3. WHY THIS EXISTS ──────────────────────────────────────────── */}
        <section className="py-28 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                  The Problem
                </span>
                <h2 className="text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                  Most businesses are underusing AI
                </h2>
              </div>
              <div className="space-y-6">
                <p className="text-body text-gray-400 leading-relaxed">
                  A lot of business owners think they are using AI because they've tried ChatGPT a few times.
                </p>
                <p className="text-body text-gray-400 leading-relaxed">
                  That is not the same as knowing how to structure context, sequence information, shape prompts, build reusable workflows, organize files, create memory layers, or connect AI to real business outcomes.
                </p>
                <p className="text-body text-gray-400 leading-relaxed">
                  The difference is not access to the tool.
                </p>
                <p className="text-body text-gray-300 leading-relaxed font-medium">
                  The difference is how you think, what you feed it, how you structure the system, and what you build around it.
                </p>
                <p className="text-body text-gray-400 leading-relaxed">
                  That is where Daniel works.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 4. EVOLUTION STORY ──────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">The Story</span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">20+ Years of Building, Testing, and Evolving</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              <div className="reveal">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 border border-[#DC2626]/40 flex items-center justify-center flex-shrink-0">
                    <Icon name="ClockIcon" size={24} className="text-[#DC2626]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Foundation</h3>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626]">Early 2000s</span>
                  </div>
                </div>
                <p className="text-body-sm text-gray-400 leading-relaxed">
                  Daniel founded Best Odds Corp in college, building niche digital media properties and performance marketing systems from scratch. Formally trained in Marketing and Business Communication at The College of St. Scholastica, he combined academic grounding with hands-on execution — building, testing, and iterating across multiple business models and affiliate systems.
                </p>
              </div>

              <div className="reveal">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 border border-[#DC2626]/40 flex items-center justify-center flex-shrink-0">
                    <Icon name="ArrowPathIcon" size={24} className="text-[#DC2626]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Evolution</h3>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626]">2010s–2020s</span>
                  </div>
                </div>
                <p className="text-body-sm text-gray-400 leading-relaxed">
                  Over the years, Daniel built, sold, traded, and repositioned multiple business ideas and systems — spanning content, e-commerce, finance, real estate, and operations. He became an active trader and content creator in the finance and trading space, developing the decision-making discipline and pattern recognition that now defines how he approaches client work.
                </p>
              </div>

              <div className="reveal">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 border border-[#DC2626]/40 flex items-center justify-center flex-shrink-0">
                    <Icon name="BoltIcon" size={24} className="text-[#DC2626]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold mb-2">Now</h3>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626]">2024–Present</span>
                  </div>
                </div>
                <p className="text-body-sm text-gray-400 leading-relaxed">
                  Today, Daniel operates Best Odds Corp as an AI-powered execution partner — building apps, Chrome extensions, automations, and growth systems using AI as leverage. He built the current Best Odds website himself using AI tools, demonstrating the same capability he brings to client work. The model: sprint-based, operator-led, and built for founders who need execution, not more advice.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── 5. OPERATOR TRAITS ──────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">What Makes Daniel Different</span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">Operator Traits That Drive Results</h2>
              <p className="text-body text-gray-400 max-w-2xl mx-auto">Not a generic agency founder. A builder, trader, and AI-fluent operator with 20+ years of pattern recognition across business models.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {operatorTraits.map((trait) => (
                <div key={trait.id} className="reveal p-8 border border-white/10 hover:border-[#DC2626]/40 transition-colors group bg-[#0D0D0D]">
                  <div className="w-12 h-12 border border-white/10 group-hover:border-[#DC2626]/40 flex items-center justify-center mb-6 transition-colors">
                    <Icon name={trait.icon as any} size={24} className="text-[#DC2626]" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{trait.title}</h3>
                  <p className="text-body-sm text-gray-400">{trait.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. CAPABILITIES ─────────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">Capabilities</span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">What Daniel Executes</h2>
              <p className="text-body text-gray-400 max-w-2xl mx-auto">Six core execution areas that remove bottlenecks and drive measurable growth.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {capabilities.map((cap) => (
                <div key={cap.id} className="reveal p-8 border border-white/10 hover:border-[#DC2626]/40 transition-colors group">
                  <div className="w-12 h-12 border border-white/10 group-hover:border-[#DC2626]/40 flex items-center justify-center mb-6 transition-colors">
                    <Icon name={cap.icon as any} size={24} className="text-[#DC2626]" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{cap.title}</h3>
                  <p className="text-body-sm text-gray-400">{cap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 7. CREDIBILITY STATS ────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">Track Record</span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">Real Credibility. Real Background.</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              <AnimatedCredStat value={20} suffix="+" label="Years Building Businesses" />
              <AnimatedCredStat value={2000} label="Best Odds Corp Founded" />
              <div className="reveal text-center p-8 border border-white/10">
                <div className="text-3xl font-bold text-[#DC2626] mb-2 leading-tight">135,658+</div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">Afterhour Followers</p>
              </div>
              <div className="reveal text-center p-8 border border-white/10">
                <div className="text-3xl font-bold text-[#DC2626] mb-2 leading-tight">17,186+</div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-400">X (Twitter) Followers</p>
              </div>
            </div>

            <div className="reveal text-center">
              <p className="text-body text-gray-400 mb-8 max-w-3xl mx-auto">
                Daniel's credibility is grounded in 20+ years of real business building — not just consulting. He has built, sold, traded, and repositioned multiple businesses. He is an active trader, content creator, and app builder. He uses AI across every workflow and built the current Best Odds website himself. He personally built the X Cleanup Console Chrome extension and other private tools — some free, some through subscription. That's the operator you're working with.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/testimonials">
                  <button className="px-8 py-4 border border-white/20 text-white text-[10px] uppercase tracking-widest font-semibold hover:border-white/50 transition-colors">
                    View Testimonials
                  </button>
                </Link>
                <Link href="/tools">
                  <button className="px-8 py-4 border border-white/20 text-white text-[10px] uppercase tracking-widest font-semibold hover:border-white/50 transition-colors">
                    View Tools Daniel Built
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── 8. HOW DANIEL WORKS ─────────────────────────────────────────── */}
        <section className="py-24 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16 text-center">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">Working Style</span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">How Daniel Works</h2>
              <p className="text-body text-gray-400 max-w-2xl mx-auto">Clear expectations, consistent cadence, no surprises.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {workingPrinciples.map((principle) => (
                <div key={principle.id} className="reveal p-8 border border-white/10 hover:border-[#DC2626]/40 transition-colors">
                  <h3 className="text-xl font-semibold mb-4">{principle.title}</h3>
                  <p className="text-body-sm text-gray-400">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 9. WHO THIS IS FOR ──────────────────────────────────────────── */}
        <section className="py-28 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                Fit
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Who this is for
              </h2>
              <p className="text-body text-gray-400">
                Best fit for businesses that want more than outsourced labor.
              </p>
            </div>

            <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
              {whoItsFor.map((item, idx) => (
                <div key={idx} className="flex items-start gap-5 p-6 border border-white/8 hover:border-[#DC2626]/30 transition-colors">
                  <Icon name="CheckCircleIcon" size={18} className="text-[#DC2626] mt-0.5 flex-shrink-0" />
                  <span className="text-body-sm text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            <div className="reveal p-8 border border-white/8 bg-[#0A0A0A]">
              <p className="text-body-sm text-gray-600 italic">
                Not ideal for buyers looking for the cheapest possible implementation or generic one-size-fits-all consulting.
              </p>
            </div>
          </div>
        </section>

        {/* ── 10. HOW TO WORK WITH DANIEL ─────────────────────────────────── */}
        <section className="py-28 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal mb-16">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                Engagement Options
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
                How to work with Daniel
              </h2>
            </div>

            <div className="reveal grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 mb-16">
              {engagementOptions.map((option) => (
                <div key={option.id} className="bg-[#0D0D0D] p-10 hover:bg-[#111] transition-colors">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                    {option.number}
                  </span>
                  <h3 className="text-xl font-semibold mb-5 tracking-tight">{option.title}</h3>
                  <p className="text-body-sm text-gray-500 leading-relaxed">{option.description}</p>
                </div>
              ))}
            </div>

            {/* Engagement & Pricing */}
            <div className="reveal border-t border-white/10 pt-20 mt-4">
              <div className="mb-12">
                <h3 className="text-3xl lg:text-4xl font-bold tracking-tight mb-6">
                  Engagement &amp; Pricing
                </h3>
                <p className="text-body text-gray-400 leading-relaxed max-w-3xl mb-3">
                  Work With Daniel is structured as a private, founder-led engagement rather than a one-size-fits-all package.
                </p>
                <p className="text-body text-gray-400 leading-relaxed max-w-3xl mb-3">
                  Engagements typically begin with a strategy conversation, then move into either ongoing advisory or implementation support depending on the business, the opportunity, and the level of involvement needed.
                </p>
                <p className="text-body text-gray-400 leading-relaxed max-w-3xl">
                  Pricing is shared after an initial fit review so scope can match the actual needs of the company.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-white/5 mb-10">
                {pricingBlocks.map((block) => (
                  <div key={block.id} className="bg-[#0D0D0D] p-10 hover:bg-[#111] transition-colors flex flex-col">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                      {block.number}
                    </span>
                    <h4 className="text-lg font-semibold mb-4 tracking-tight">{block.title}</h4>
                    <p className="text-body-sm text-gray-500 leading-relaxed mb-8 flex-1">{block.description}</p>
                    <div className="border-t border-white/10 pt-6">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-semibold">{block.pricing}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mb-14 p-6 border-l-2 border-[#DC2626] bg-[#0D0D0D]">
                <p className="text-body-sm text-gray-300 font-medium">
                  Limited founder capacity. Best fit for businesses ready to implement and move quickly.
                </p>
              </div>

              <div className="bg-[#0D0D0D] border border-white/10 p-12">
                <h4 className="text-2xl lg:text-3xl font-bold tracking-tight mb-4">
                  Interested in working directly with Daniel?
                </h4>
                <p className="text-body text-gray-400 leading-relaxed mb-8 max-w-xl">
                  Start with a conversation to determine fit, scope, and the highest-leverage engagement path.
                </p>
                <Link href="/book">
                  <button className="px-10 py-4 bg-[#DC2626] text-white text-[10px] uppercase tracking-widest font-semibold hover:bg-[#b91c1c] transition-colors">
                    Book a Consultation
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ── 11. NEED EXECUTION TOO ──────────────────────────────────────── */}
        <section className="py-28 px-6 border-b border-white/10">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
              <div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                  Strategy + Execution
                </span>
                <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-8 leading-tight">
                  Need execution too?
                </h2>
                <p className="text-body text-gray-400 leading-relaxed mb-6">
                  Some clients work directly with Daniel for high-level strategy. Others use that strategy to guide a broader Best Odds Corp engagement across marketing, creative, websites, funnels, content, research, or operations.
                </p>
                <p className="text-body text-gray-400 leading-relaxed mb-10">
                  If you need both thinking and execution, Best Odds can do both.
                </p>
                <Link href="/services">
                  <button className="px-8 py-4 border border-white/20 text-white text-[10px] uppercase tracking-widest font-semibold hover:border-white/50 transition-colors">
                    View Full Services
                  </button>
                </Link>
              </div>
              <div className="space-y-4">
                {['Marketing & Paid Media', 'Creative & Content Production', 'Websites & Funnels', 'Research & Strategy', 'Operations & Systems'].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-5 border border-white/8">
                    <span className="w-1.5 h-1.5 bg-[#DC2626] flex-shrink-0" />
                    <span className="text-body-sm text-gray-400">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 12. FAQ ─────────────────────────────────────────────────────── */}
        <section className="py-28 px-6 border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-[900px] mx-auto">
            <div className="reveal mb-16">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-6">
                Questions
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="reveal space-y-px">
              {faqData.map((item) => (
                <div key={item.id} className="border border-white/8 bg-[#0A0A0A]">
                  <button
                    onClick={() => toggleFaq(item.id)}
                    className="w-full flex items-center justify-between p-8 text-left hover:bg-[#111] transition-colors"
                  >
                    <span className="text-base font-medium tracking-tight pr-8">{item.question}</span>
                    <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center text-[#DC2626] transition-transform duration-200 ${openFaq === item.id ? 'rotate-45' : ''}`}>
                      <Icon name="PlusIcon" size={18} />
                    </span>
                  </button>
                  {openFaq === item.id && (
                    <div className="px-8 pb-8">
                      <div className="w-8 h-[1px] bg-[#DC2626] mb-6" />
                      <p className="text-body-sm text-gray-400 leading-relaxed">{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 13. CLOSING CTA ─────────────────────────────────────────────── */}
        <section className="py-28 px-6">
          <div className="max-w-[1200px] mx-auto">
            <div className="reveal text-center max-w-3xl mx-auto">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#DC2626] block mb-8">
                Start Here
              </span>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-8 leading-tight">
                If you want execution and accountability, let's talk.
              </h2>
              <p className="text-body text-gray-400 mb-12 leading-relaxed">
                Every engagement starts with a Sprint — a paid diagnostic that maps your constraints and delivers a clear execution roadmap. No pressure to commit to a retainer before we understand your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link href="/book">
                  <button className="px-10 py-4 bg-[#DC2626] text-white text-[10px] uppercase tracking-widest font-semibold hover:bg-[#b91c1c] transition-colors">
                    Book a Sprint — Start With a Diagnostic
                  </button>
                </Link>
                <Link href="/services">
                  <button className="px-10 py-4 border border-white/20 text-white text-[10px] uppercase tracking-widest font-semibold hover:border-white/50 transition-colors">
                    View Services
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
